package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.navigation.NavHostController
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.settings.SettingsBillingSection
import org.catharsis.spendtel.feature.presentation.settings.DeleteAccountDialog
import org.catharsis.spendtel.feature.presentation.settings.SettingsDeleteAccountSection
import org.catharsis.spendtel.feature.presentation.settings.SettingsLegalFooter
import org.catharsis.spendtel.feature.presentation.settings.SettingsSupportRow
import org.catharsis.spendtel.feature.presentation.settings.rememberSettingsScreenBindings

private val webSettingsMaxWidth = 720.dp

@Composable
fun WebSettingsScreen(navHostController: NavHostController) {
    val bindings = rememberSettingsScreenBindings(navHostController)
    val scrollState = rememberScrollState()
    val chrome = rememberWebSessionChrome(navHostController)
    var showDeleteAccountDialog by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .imePadding(),
    ) {
        WebAmbientPageBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            WebAppChrome(
                userName = chrome.userName,
                email = chrome.email,
                creditsSummary = chrome.creditsSummary,
                accountMenuExpanded = chrome.accountMenuExpanded,
                onLogoClick = chrome.onLogoClick,
                onAccountMenuToggle = chrome.onAccountMenuToggle,
                onAccountMenuDismiss = chrome.onAccountMenuDismiss,
                onOpenSettings = chrome.onOpenSettings,
                onSignOut = chrome.onSignOut,
            )

            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
            ) {
                WebViewportScrollColumn(
                    scrollState = scrollState,
                    modifier = Modifier.fillMaxSize(),
                    maxContentWidth = webSettingsMaxWidth,
                ) {
                    Column(
                        modifier = Modifier.padding(horizontal = 24.dp, vertical = 32.dp),
                        verticalArrangement = Arrangement.spacedBy(28.dp),
                    ) {
                        WebBackLink(
                            label = "Back to dashboard",
                            onClick = { navHostController.popBackStack() },
                        )

                        WebPageBreadcrumb(
                            segments = listOf("Dashboard", "Settings"),
                        )

                        WebSectionHeader(
                            title = "Settings",
                            subtitle = "Manage your report credits and spending goal.",
                        )

                        WebAccentCard {
                            Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                                Text(
                                    text = "Reports & billing",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = SpendTelReportColors.ink,
                                )
                                Text(
                                    text = "Each credit lets you upload statements and generate one spending story.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = SpendTelReportColors.inkMuted,
                                    lineHeight = MaterialTheme.typography.bodyMedium.lineHeight,
                                )
                                SettingsBillingSection(
                                    entitlement = bindings.entitlement,
                                    isPurchasing = bindings.isPurchasing,
                                    onBuyReport = bindings.onBuyReport,
                                    buyButtonFullWidth = false,
                                )
                            }
                        }

                        WebAccentCard {
                            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                Text(
                                    text = "Personalization",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = SpendTelReportColors.ink,
                                )
                                Text(
                                    text = "Your goal is saved to your profile and used when generating spending stories and insights.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = SpendTelReportColors.inkMuted,
                                    lineHeight = MaterialTheme.typography.bodyMedium.lineHeight,
                                )
                                HorizontalDivider(
                                    color = SpendTelReportColors.inkMuted.copy(alpha = 0.12f),
                                )
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable(onClick = bindings.onChangeGoal)
                                        .padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                        Text(
                                            text = "Financial goal",
                                            style = MaterialTheme.typography.labelLarge,
                                            color = SpendTelReportColors.inkMuted,
                                        )
                                        Text(
                                            text = bindings.goalTitle,
                                            style = MaterialTheme.typography.titleSmall,
                                            fontWeight = FontWeight.SemiBold,
                                            color = SpendTelReportColors.ink,
                                        )
                                    }
                                    Icon(
                                        imageVector = Icons.Outlined.ChevronRight,
                                        contentDescription = null,
                                        tint = SpendTelReportColors.inkMuted,
                                    )
                                }
                            }
                        }

                        WebAccentCard {
                            Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                                Text(
                                    text = "Account",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = SpendTelReportColors.ink,
                                )
                                HorizontalDivider(
                                    color = SpendTelReportColors.inkMuted.copy(alpha = 0.12f),
                                )
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable {
                                            navHostController.navigate(AppRoutes.ContactSupport.name)
                                        }
                                        .padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    SettingsSupportRow(modifier = Modifier.weight(1f))
                                }
                                HorizontalDivider(
                                    color = SpendTelReportColors.inkMuted.copy(alpha = 0.12f),
                                )
                                SettingsDeleteAccountSection(
                                    isDeleting = bindings.isDeletingAccount,
                                    onDeleteAccount = { showDeleteAccountDialog = true },
                                )
                            }
                        }

                        SettingsLegalFooter(
                            onPrivacyClick = {
                                navHostController.navigate(AppRoutes.PrivacyPolicy.name)
                            },
                            onTermsClick = {
                                navHostController.navigate(AppRoutes.TermsAndConditions.name)
                            },
                        )
                    }
                }
            }
        }

        SnackbarHost(
            hostState = bindings.snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter),
        )

        bindings.paywall?.let { paywall ->
            WebReportPaywallModal(
                message = paywall.message,
                priceLabel = paywall.priceLabel,
                isPurchasing = bindings.isPurchasing,
                errorMessage = paywall.errorMessage,
                onDismiss = bindings.onDismissPaywall,
                onPurchase = bindings.onPurchaseReport,
            )
        }

        if (showDeleteAccountDialog) {
            DeleteAccountDialog(
                isDeleting = bindings.isDeletingAccount,
                onDismiss = { showDeleteAccountDialog = false },
                onConfirmDelete = {
                    bindings.onDeleteAccount()
                    showDeleteAccountDialog = false
                },
            )
        }
    }
}
